<template>
  <v-dialog v-model="active" width="800">
    <v-card style="height:800px">
      <v-toolbar class="mb-2">
        <v-toolbar-title>{{ name }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="active = false">
          <v-icon>fal fa-times</v-icon>
        </v-btn>
      </v-toolbar>
      <v-tabs v-model="tab">
        <v-tab>Description</v-tab>
        <v-tab>Performances</v-tab>
        <v-tab>Performers/Discounts</v-tab>
        <v-tab>Confirmation</v-tab>
      </v-tabs>
      <v-tabs-items v-model="tab">
        <v-tab-item>
          <v-img v-if="image" :src="`/img/theatre/${showId}.jpg`" height="300">
            <div style="float:right">
              <v-btn color="success" small @click="imageDialog = true">
                <v-icon left>fas fa-image</v-icon>
                Change Image
              </v-btn>
              <v-btn color="error" small @click="removeImage()">
                <v-icon left>fal fa-times</v-icon>
                Remove Image
              </v-btn>
            </div>
          </v-img>
          <v-btn v-else color="success" @click="imageDialog = true">
            <v-icon left>fas fa-image</v-icon>
            Add Image
          </v-btn>
          <v-dialog v-model="imageDialog" width="350">
            <v-card>
              <v-card-title>Upload Cover Image</v-card-title>
              <v-card-text>
                <v-file-input v-model="imageUpload" label="Upload Image" accept="image/jpeg" outlined hide-details />
              </v-card-text>
              <v-card-actions>
                <v-btn text @click="uploadImage">Upload</v-btn>
                <v-spacer />
                <v-btn text @click="imageDialog = false">Cancel</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <html-editor v-model="description" />
          <v-btn @click="saveChanges()">Save Changes</v-btn>
        </v-tab-item>
        <v-tab-item>
          <v-expansion-panels>
            <v-expansion-panel v-for="performance in performances" :key="performance._id">
              <v-expansion-panel-header>
                <v-row>
                  <v-col :cols="5">{{ performance.dateStr }}</v-col>
                  <v-col>
                    <v-row>
                      <v-col>Seats: {{ performance.tickets }}</v-col>
                      <v-col>Reserved: {{ performance.reserved }}</v-col>
                      <v-col>Seats Left: {{ performance.seatsLeft }}</v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row v-for="{ _id, label, price, limit } in performance.prices" :key="_id">
                  <v-col>
                    <v-combobox :value="label" :items="['Covenant Student', 'Covenant Faculty/Staff', 'Community Adult', 'Community Student/Elder']" label="Price Label" outlined hide-details @change="(label) => updatePrice(performance._id, _id, { label })" />
                  </v-col>
                  <v-col>
                    <v-text-field :value="price" label="Price Amount" type="number" outlined hide-details @change="(price) => updatePrice(performance._id, _id, { price })" />
                  </v-col>
                  <v-col>
                    <v-text-field :value="limit" label="Ticket Limit" type="number" hint="Leave blank for no limit" outlined hide-details clearable @change="(limit) => updatePrice(performance._id, _id, { limit })">
                      <template #append-outer>
                        <v-tooltip top>
                          <template #activator="{ on }">
                            <v-btn v-on="on" icon style="margin-top:-8px" @click="removePriceOption(_id, performance._id)">
                              <v-icon color="error">fas fa-times-circle</v-icon>
                            </v-btn>
                          </template>
                          <span>Remove Price Option</span>
                        </v-tooltip>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>
                <v-toolbar flat dense class="mt-4">
                  <v-btn @click="addPrice(performance._id)">Add Price Option</v-btn>
                  <v-spacer />
                  <v-btn :disabled="performance.reserved > 0" @click="removePerformance(performance._id)">Remove Performance</v-btn>
                </v-toolbar>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
          <v-dialog v-model="addPerformanceDialog" width="350">
            <template #activator="{ on }">
              <v-btn v-on="on" class="ma-2 mb-4">Add Performance</v-btn>
            </template>
            <v-card>
              <v-card-title>Add Performance</v-card-title>
              <v-card-text>
                <date-picker v-model="addPerfDate" label="Performance Date" />
                <time-picker v-model="addPerfTime" label="Performance Time" />
                <v-text-field v-model="addPerfTickets" label="Reservable Seats" type="number" min="1" outlined />
              </v-card-text>
              <v-card-actions>
                <v-btn :disabled="!(addPerfDate && addPerfTime && addPerfTickets > 0)" @click="addPerformance()">Add Performance</v-btn>
                <v-spacer />
                <v-btn @click="addPerformanceDialog = false">Cancel</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-tab-item>
        <v-tab-item>
          <v-data-table :items="discounts" :headers="discountHeaders">
            <template #item.used="{ item }">
              <v-icon :color="item.used ? 'success' : 'error'">fas fa-{{ item.used ? 'check' : 'minus' }}</v-icon>
            </template>
            <template #item.action="{ item }">
              <v-btn v-if="!item.used" icon @click="editDiscount(item)">
                <v-icon>fal fa-pencil</v-icon>
              </v-btn>
            </template>
          </v-data-table>
          <v-dialog v-model="discountDialog" persistent width="800">
            <template #activator="{ on }">
              <v-btn v-on="on" class="ma-4">Add Discount</v-btn>
            </template>
            <v-card>
              <v-card-title>{{ discountEditId ? 'Edit' : 'Add' }} Discount</v-card-title>
              <v-card-text>
                <p>Discounts are added via email address. An email will be sent to them with the information on how to use the discount. Students/Employees with a discount code can use it when they log in, or the email will also include a code that they can send to someone else to use (like parents).</p>
                <v-row>
                  <v-col>
                    <v-select v-model="discountType" :items="discountTypeOptions" :disabled="!!discountEditId" label="Discount Type" outlined dense />
                    <!-- <v-switch v-model="discountType" :label="`User ${discountCovenant ? 'has' : 'doesn\'t have'} a Covenant account`" /> -->
                     <v-text-field v-if="discountType === 'performer' && !!discountEditId" v-model="discountEmail" label="Covenant Email" disabled outlined dense />
                    <directory-search v-else-if="discountType === 'performer'" v-model="discountUser" label="Directory Search" :person-types="['Faculty','Staff','Student']" dense />
                    <v-text-field v-else-if="discountType === 'other'" v-model="discountEmail" :disabled="!!discountEditId" label="Non-Covenant Email" outlined dense />
                    <v-text-field v-model="discountPrice" label="Discounted Price" type="number" min="0" outlined dense />
                    <v-text-field v-model="discountLimit" label="Ticket Limit" type="number" hint="How many at this price" min="1" clearable persistent-hint outlined dense />
                  </v-col>
                  <v-col>
                    <h4>Eligible Performances</h4>
                    <v-list dense class="py-0">
                      <v-list-item v-for="{ dateStr, _id } of performances" :key="_id" @click="toggleDiscountPerformance(_id)">
                        <v-list-item-action style="max-height:16px;margin-right:12px">
                          <v-icon>fal fa-{{ discountPerformances.includes(_id) ? 'check-' : '' }}square</v-icon>
                        </v-list-item-action>
                        <v-list-item-title>{{ dateStr }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                    <v-btn dense :disabled="performances.length === discountPerformances.length" @click="discountPerformances = performances.map(({ _id }) => _id)">Select All</v-btn>
                    <v-btn dense :disabled="discountPerformances.length === 0" @click="discountPerformances = []">Deselect All</v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
              <v-card-actions>
                <v-btn :disabled="!validDiscount" @click="saveDiscount">{{ discountEditId ? 'Update' : 'Add' }} Discount</v-btn>
                <v-spacer />
                <v-btn v-if="discountEditId" @click="removeDiscount({ _id: discountEditId })" color="error">Remove Discount</v-btn>
                <v-spacer />
                <v-btn @click="discountDialog = false; discountEditId = null">{{ discountEditId ? 'Cancel' : 'Close' }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-tab-item>
        <v-tab-item>
          <v-card-text>
            <v-label>Confirmation Text</v-label>
            <p>This is the text the customer will see on the page after completing their purchase/reservation.</p>
            <html-editor v-model="confirmation.text" label="Confirmation Text" outlined />
            <v-label class="mt-6" style="display:block">Confirmation Email</v-label>
            <p>If you want the customer to receive an email, complete the following two blocks. This email will be sent from report-email@covenant.edu. If either block is empty, then no email will be sent except for the one from PayPal.</p>
            <v-text-field v-model="confirmation.subject" label="Subject" outlined class="mt-2" />
            <v-label class="mb-2" style="display:block;">Email Body</v-label>
            <html-editor v-model="confirmation.body" outlined />
          </v-card-text>
          <v-card-actions>
            <v-btn @click="updateConfirmation">Save Changes</v-btn>
          </v-card-actions>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </v-dialog>
</template>
<script>
import { ref, computed, watch } from '@vue/composition-api'
import axios from 'axios'

export default {
  components: {
    HtmlEditor: () => import('@/components/forms/HTMLEditor'),
    DirectorySearch: () => import('@/components/forms/inputs/DirectorySearch'),
    DatePicker: () => import('@/components/forms/inputs/DatePicker'),
    TimePicker: () => import('@/components/forms/inputs/TimePicker')
  },
  props: {
    showId: { type: String, required: true },
    value: Boolean
  },
  setup (props, { root, emit }) {
    const user = computed(() => 'spoof' in root.$store.state.user ? root.$store.state.user.spoof : root.$store.state.user)
    const isAdmin = computed(() => user.value.roles.includes('Technology Services'))

    const active = ref(false)

    watch(() => props.value, () => {
      console.log(`props.value changed: ${props.value}`)
      if (props.value !== active.value) active.value = props.value
    })
    watch(active, () => {
      if (props.value !== active.value) emit('input', active.value)
    })

    const tab = ref(0)
    const name = ref('')
    const year = ref('')
    const shortName = ref('')
    const showHeader = ref(true)
    const image = ref(true)
    const description = ref('')
    const performances = ref([])
    const isLoaded = ref(false)

    function parseRes (res) {
      name.value = res.name
      year.value = res.year
      shortName.value = res.shortName
      showHeader.value = res.showHeader
      image.value = res.image == null ? true : res.image
      description.value = res.description
      performances.value = res.performances.map((row) => {
        const dt = new Date(row.date)
        const date = dt.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' })
        const time = dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })
        row.dateStr = `${date} ${time}`
        row.seatsLeft = row.tickets - row.reserved
        return row
      }).sort(({ date: aDate }, { date: bDate }) => new Date(aDate) < new Date(bDate) ? -1 : 1)
      discounts.value = res.discounts.filter(({ code }) => !!code) || []
      if (res.confirmation) confirmation.value = res.confirmation
    }

    watch(() => props.showId, async () => {
      if (props.showId) {
        const res = await root.$feathers.service('theatre/show').get(props.showId)
        parseRes(res)
        setTimeout(() => { isLoaded.value = true }, 200)
      }
    })

    watch(name, () => {
      if (isLoaded.value) patch({ name: name.value })
    })
    watch(shortName, () => {
      if (isLoaded.value) patch({ shortName: shortName.value })
    })
    watch(description, () => {
      if (isLoaded.value) patch({ description: description.value })
    })

    async function patch (patch) {
      const res = await root.$feathers.service('theatre/show').patch(props.showId, patch)
      parseRes(res)
      emit('update', res)
      return res
    }

    const imageDialog = ref(false)
    const imageUpload = ref(null)
    async function uploadImage () {
      if (imageUpload.value) {
        // Upload the file
        const formData = new FormData()
        formData.append('folder', 'theatre')
        formData.append('filename', props.showId)
        formData.append('file', imageUpload.value)

        image.value = true
        patch({ image: true })
        const token = window.localStorage['feathers-jwt']
        const basePath = process.env.VUE_APP_API_URL + process.env.VUE_APP_API_PATH.replace('/socket.io', '')
        imageDialog.value = false
        await axios.post(basePath + '/system/image-upload', formData, { headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` } })
      }
    }

    function removeImage () {
      image.value = false
      patch({ image: false })
    }

    const addPerformanceDialog = ref(false)
    const addPerfDate = ref('')
    const addPerfTime = ref('')
    const addPerfTickets = ref(200)
    async function addPerformance () {
      const date = new Date(addPerfDate.value + ' ' + addPerfTime.value)
      await patch({ $push: { performances: { date, tickets: addPerfTickets.value } } })
      addPerformanceDialog.value = false
    }
    async function removePerformance (_id) {
      if (confirm('Are you sure you want to remove this performance? This action cannot be undone.')) {
        await patch({ $pull: { performances: { _id } } })
      }
    }

    async function updatePrice (performanceId, priceId, upd) {
      const patch = {}
      for (const field in upd) {
        patch[`performances.$[perf].prices.$[price].${field}`] = upd[field]
      }
      const arrayFilters = [{ 'perf._id': performanceId }, { 'price._id': priceId }]
      const res = await root.$feathers.service('theatre/show').patch(props.showId, patch, { query: { arrayFilters } })
      parseRes(res)
    }

    async function addPrice (performanceId) {
      console.log(`Adding price for performance ${performanceId}`)
      const patch = { $push: { 'performances.$[perf].prices': { label: '', price: 0 } } }
      const arrayFilters = [{ 'perf._id': performanceId }]
      const res = await root.$feathers.service('theatre/show').patch(props.showId, patch, { query: { arrayFilters } })
      parseRes(res)
    }

    async function removePriceOption (priceId, performanceId) {
      const patch = { $pull: { 'performances.$[perf].prices': { _id: priceId } } }
      const arrayFilters = [{ 'perf._id': performanceId }]
      const res = await root.$feathers.service('theatre/show').patch(props.showId, patch, { query: { arrayFilters } })
      parseRes(res)
    }

    const discounts = ref([])
    const discountHeaders = ref([
      { text: 'Email', value: 'email' },
      { text: 'Code', value: 'code' },
      { text: 'Discounted Price', value: 'discountedPrice' },
      { text: 'Limit', value: 'limit' },
      { text: 'Used', value: 'used' },
      { text: 'Edit', value: 'action' }
    ])

    async function removeDiscount ({ _id }) {
      const res = await root.$feathers.service('theatre/show').patch(props.showId, { $pull: { 'discounts': { _id } } })
      discountDialog.value = false
      discountEditId.value = null
      parseRes(res)
    }

    const discountDialog = ref(false)
    const discountType = ref('performer')
    const discountTypeOptions = ref([{ text: 'Covenant Email', value: 'performer' }, { text: 'Non-Covenant Email', value: 'other' }, { text: 'Student Discount', value: 'student' }, { text: 'COR160 Student Discount', value: 'cor160' }, { text: 'Employee Discount', value: 'employee' }])
    const discountUser = ref(null)
    const discountEmail = ref('')
    const discountPrice = ref('0')
    const discountLimit = ref('2')
    const discountPerformances = ref([])
    const discountPerformanceOptions = computed(() => performances.value.map(({ dateStr, _id }) => { return { text: dateStr, value: _id } }))
    const discountEditId = ref('')

    async function editDiscount ({ _id, email, discountedPrice, limit, performances, used }) {
      if (used) {
        alert('You cannot edit a used discount')
        return
      }
      if (email === 'Student Discount') {
        discountType.value = 'student'
      } else if (email === 'COR160 Student Discount') {
        discountType.value = 'cor160'
      } else if (email === 'Employee Discount') {
        discountType.value = 'employee'
      } else {
        discountType.value = /@covenant.edu/.test(email) ? 'performer' : 'other'
        discountEmail.value = email
      }
      discountPrice.value = discountedPrice
      discountLimit.value = limit
      discountPerformances.value = performances
      discountEditId.value = _id
      discountDialog.value = true
    }

    const validDiscount = computed(() => {
      if ((discountType.value === 'performer' && !discountUser.value) || (discountType.value === 'other' && !discountEmail.value) || discountPerformances.value.length === 0 || (!discountPrice.value)) return false
      if (discountType.value === 'other') {
        const regex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/
        return regex.test(discountEmail.value)
      }
      return true
    })

    function toggleDiscountPerformance (_id) {
      const index = discountPerformances.value.findIndex((id) => id === _id)
      if (index >= 0) {
        discountPerformances.value.splice(index, 1)
      } else {
        discountPerformances.value.push(_id)
      }
    }

    async function removeDiscountUser (email) {
      console.log(email)
    }
    async function saveDiscount () {
      const patch = {}
      const query = {}
      if (discountEditId.value) {
        patch['discounts.$[disc].discountedPrice'] = discountPrice.value
        patch['discounts.$[disc].limit'] = discountLimit.value
        patch['discounts.$[disc].performances'] = discountPerformances.value
        query.arrayFilters = [{ 'disc._id': discountEditId.value }]
      } else {
        const code = await generateDiscountCode()
        const obj = {
          discountedPrice: discountPrice.value,
          limit: discountLimit.value,
          performances: discountPerformances.value,
          code
        }
        if (discountType.value === 'performer') {
          const res = await root.$feathers.service('directory/people').get(discountUser.value)
          if (res && res.email) obj.email = res.email
        } else if (discountType.value === 'other') {
          obj.email = discountEmail.value
        } else {
          obj.email = discountTypeOptions.value.find(({ value }) => value === discountType.value).text
        }
        patch.$push = { discounts: obj }
      }
      const res = await root.$feathers.service('theatre/show').patch(props.showId, patch, { query })
      parseRes(res)
      discountUser.value = ''
      discountEmail.value = ''
      discountEditId.value = null
      discountDialog.value = false
    }

    async function generateDiscountCode () {
      const code = Math.random().toString(36).substring(2, 10).toUpperCase()
      const { total } = root.$feathers.service('theatre/show').find({ query: { aggregate: [{ $match: { 'discountCodes.code': code, year: year.value } }] } })
      if (total > 0) {
        return generateDiscountCode()
      }
      return code
    }

    const confirmation = ref({ text: '', subject: '', body: '' })
    async function updateConfirmation () {
      await root.$feathers.service('theatre/show').patch(props.showId, { confirmation: confirmation.value })
      root.$store.dispatch('main/snackbar', { color: 'success', text: 'Confirmation details updated.' })
    }

    return {
      user,
      isAdmin,
      active,
      tab,
      name,
      shortName,
      showHeader,
      description,
      performances,
      isLoaded,
      image,
      imageDialog,
      imageUpload,
      uploadImage,
      removeImage,
      addPerformanceDialog,
      addPerfDate,
      addPerfTime,
      addPerfTickets,
      addPerformance,
      removePerformance,
      updatePrice,
      addPrice,
      removePriceOption,
      discounts,
      discountHeaders,
      removeDiscount,
      discountDialog,
      discountType,
      discountTypeOptions,
      discountUser,
      discountEmail,
      discountPrice,
      discountLimit,
      discountPerformances,
      discountPerformanceOptions,
      discountEditId,
      editDiscount,
      validDiscount,
      toggleDiscountPerformance,
      removeDiscountUser,
      saveDiscount,
      confirmation,
      updateConfirmation
    }
  }
}
</script>
